import React, { useState, useEffect } from 'react';
import { AnalysisStage } from '../../types';
import { SIGNATURE_MESSAGES_FR, SIGNATURE_MESSAGES_EN } from '../../services/legendsData';

interface NeuralScannerProps {
    stage: AnalysisStage | string;
    lang: 'fr' | 'en';
    isRecalibration?: boolean;
    labelOverride?: string | null;
}

export const NeuralScanner: React.FC<NeuralScannerProps> = ({ stage, lang, isRecalibration = false, labelOverride }) => {
    const isFr = lang === 'fr';
    const messages = isFr ? SIGNATURE_MESSAGES_FR : SIGNATURE_MESSAGES_EN;
    const [msgIndex, setMsgIndex] = useState(0);
    
    useEffect(() => {
        const t = setInterval(() => setMsgIndex(p => (p + 1) % messages.length), 3000);
        return () => clearInterval(t);
    }, [messages.length]);

    const recalibrationMsg = isFr ? "Recalibration de la sévérité..." : "Recalibrating severity thresholds...";

    return (
        <div className="fixed inset-0 z-[999999] flex flex-col items-center justify-center bg-[#010203]/98 backdrop-blur-2xl text-center animate-fade-in">
            <div className="w-full max-w-4xl px-8 space-y-12">
                <div className="inline-flex items-center gap-4 px-8 py-3 rounded-full bg-white/10 border border-white/20 backdrop-blur-3xl shadow-[0_0_50px_rgba(34,211,238,0.2)]">
                    <div className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping"></div>
                    <span className="text-[10px] font-black text-white uppercase tracking-[0.5em] italic">
                        {isRecalibration ? 'Neural Core Re-tuning' : 'Neural Computation Slot Active'}
                    </span>
                </div>
                
                <div className="space-y-6">
                    <div className="flex flex-col items-center gap-2">
                        <div className="text-cyan-400 text-[11px] font-black uppercase tracking-[0.6em] animate-pulse h-6">
                            {labelOverride || (isRecalibration ? 'RE-SCORING' : stage)}
                        </div>
                        <div className="w-48 h-1 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full bg-cyan-500 animate-shimmer-fast w-full shadow-[0_0_15px_#06b6d4]"></div>
                        </div>
                    </div>
                    
                    <h2 className="text-4xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-tight min-h-[3em] drop-shadow-2xl">
                        {isRecalibration ? recalibrationMsg : messages[msgIndex]}
                    </h2>
                </div>

                {isRecalibration && (
                    <div className="p-8 bg-smart-gold/5 border border-smart-gold/20 rounded-[40px] max-w-lg mx-auto shadow-inner">
                        <p className="text-[10px] text-smart-gold font-black uppercase tracking-[0.4em] leading-relaxed italic">
                            L'IA re-évalue votre performance selon les nouveaux critères du niveau choisi. <br/>Veuillez patienter...
                        </p>
                    </div>
                )}
            </div>
            <style>{`
                @keyframes shimmer-fast {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                .animate-shimmer-fast {
                    animation: shimmer-fast 1s infinite linear;
                }
            `}</style>
        </div>
    );
};
